import { filter } from 'es-toolkit/compat';
import { distinctStrArr } from './vanillaJs';

type StringKeyOf<T> = Extract<
  {
    [K in keyof T]: T[K] extends string ? K : never;
  }[keyof T],
  string
>;

type GetCachedItemsByIdsArgs<TItem extends object, TKey extends StringKeyOf<TItem>> = {
  ids: (string | null | undefined)[];
  cachedItems: TItem[];
  fetchByIds: (ids: string[]) => Promise<TItem[]>;
} & (
  | { getKey: (item: TItem) => string | null | undefined; predicate?: never }
  | ('id' extends StringKeyOf<TItem>
      ? { predicate?: TKey; getKey?: never }
      : { predicate: TKey; getKey?: never })
);

/** Use `getKey` for nested/legacy-aware keys (e.g. `resource.id`), `predicate` for top-level keys. */
export async function getCachedItemsByIds<
  TItem extends object,
  TKey extends StringKeyOf<TItem> = StringKeyOf<TItem>
>({ predicate, getKey, ids, cachedItems, fetchByIds }: GetCachedItemsByIdsArgs<TItem, TKey>) {
  const uniqueIds = distinctStrArr(ids);
  const resolvedPredicate = (predicate ?? 'id') as TKey;

  if (!uniqueIds.length) {
    return [] as TItem[];
  }

  const getItemKey = (item: TItem) =>
    (getKey ? getKey(item) : (item[resolvedPredicate] as string)) as string;
  const matchingCachedItems = filter(cachedItems, (item) => uniqueIds.includes(getItemKey(item)));
  const cachedItemsById = new Map<string, TItem>(
    matchingCachedItems.map((item): [string, TItem] => [getItemKey(item), item])
  );
  const missingIds = uniqueIds.filter((id) => !cachedItemsById.has(id));

  if (!missingIds.length) {
    return uniqueIds.map((id) => cachedItemsById.get(id)).filter((item): item is TItem => !!item);
  }

  const fetchedItems = await fetchByIds(missingIds);
  const mergedItemsById = new Map<string, TItem>([
    ...cachedItemsById.entries(),
    ...fetchedItems.map((item): [string, TItem] => [getItemKey(item), item])
  ]);

  return uniqueIds.map((id) => mergedItemsById.get(id)).filter((item): item is TItem => !!item);
}
